import { postMessageWithResponse } from '@common/services/post-message-to-whatsapp-functions';
import type { FetchAssetBlobResult as ContractFetchAssetBlobResult } from '@bt-common/interfaces/message-command-contracts.interface';

/**
 * Timing the content-script handler measured around its own fetch, returned
 * next to the bytes (or the failure) so the page side can say WHICH hop was
 * slow. Local to the extension: the bt-common contract only types `success`,
 * `error` and `data`.
 */
export interface FetchAssetBlobDiag {
  host: string;
  httpStatus?: number;
  fetchMs: number;
  readMs?: number;
  encodeMs?: number;
  bytes?: number;
}

/** bt-common's contract result plus the extension-local `diag` (adding it to
 *  bt-common would need a separate submodule PR; the field is optional, so the
 *  contract stays compatible). */
export interface FetchAssetBlobResult extends ContractFetchAssetBlobResult {
  diag?: FetchAssetBlobDiag;
}

export const isFetchAssetBlobResult = (v: unknown): v is FetchAssetBlobResult =>
  typeof v === 'object' && v !== null && typeof (v as { success?: unknown }).success === 'boolean';

/** Total content-side time across the measured steps. */
export const diagTotalMs = (d: FetchAssetBlobDiag): number => d.fetchMs + (d.readMs ?? 0) + (d.encodeMs ?? 0);

export const SLOW_FETCH_WARN_MS = 5_000;

/**
 * The page → content-script → fetch round trip used to ride the 10s default
 * of postMessageWithResponse (#480): a slow GCS read, or a content script that
 * is busy while WhatsApp Web (re)opens, tripped it and the send failed as a
 * system fault. Media is read in full and base64-encoded in the content script,
 * so give it more room than a plain bus call.
 */
export const FETCH_ASSET_BLOB_TIMEOUT_MS = 30_000;

export const assetHost = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return 'invalid-url';
  }
};

type PostFn = (
  envelope: { cmd: 'fetchAssetBlob'; data: { url: string }; timeoutOverride: number },
) => Promise<unknown>;

interface Deps {
  post?: PostFn;
  now?: () => number;
}

/**
 * Ask the content script for the asset bytes. A bus timeout is re-thrown with
 * the asset host, elapsed time and the hop that went silent appended (the
 * original message stays the prefix so existing error matching still works).
 * Returns undefined only when the bus resolves with no payload.
 */
export async function requestAssetBlob(url: string, deps: Deps = {}): Promise<FetchAssetBlobResult | undefined> {
  const post: PostFn = deps.post ?? ((env) => postMessageWithResponse(env));
  const now = deps.now ?? (() => performance.now());
  const host = assetHost(url);
  const t0 = now();
  let raw: unknown;
  try {
    raw = await post({
      cmd: 'fetchAssetBlob',
      data: { url },
      timeoutOverride: FETCH_ASSET_BLOB_TIMEOUT_MS,
    });
  } catch (err) {
    const elapsedMs = Math.round(now() - t0);
    const base = err instanceof Error ? err.message : String(err);
    throw new Error(
      `${base} [fetchAssetBlob host=${host} elapsedMs=${elapsedMs} hop=content-script-no-reply]`,
      { cause: err },
    );
  }
  const totalMs = Math.round(now() - t0);
  const result = isFetchAssetBlobResult(raw) ? raw : undefined;
  if (result && (!result.success || totalMs > SLOW_FETCH_WARN_MS)) {
    console.warn('[fetchAssetBlob][page]', { host, totalMs, success: result.success, error: result.error, ...result.diag });
  }
  return result;
}

const hex = (bytes: Uint8Array, n = 4): string =>
  Array.from(bytes.subarray(0, n), (b) => b.toString(16).padStart(2, '0')).join('');

/** Leading bytes of the image types we can identify. */
const IMAGE_MAGIC: ReadonlyMap<string, readonly number[]> = new Map([
  ['image/jpeg', [0xff, 0xd8, 0xff]],
  ['image/png', [0x89, 0x50, 0x4e, 0x47]],
  ['image/gif', [0x47, 0x49, 0x46, 0x38]],
  // RIFF....WEBP — only the RIFF prefix is checked
  ['image/webp', [0x52, 0x49, 0x46, 0x46]],
]);

const startsWith = (bytes: Uint8Array, magic: readonly number[]): boolean =>
  bytes.length >= magic.length && magic.every((b, i) => bytes[i] === b);

/** Why the body cannot be the image it claims to be, or null when it looks intact. */
const integrityProblem = (bytes: Uint8Array, mimeType: string, contentLength: number | null, encoded: boolean): string | null => {
  const type = mimeType.split(';')[0].trim().toLowerCase();
  // Content-Length of a content-encoded body is the encoded size, not the decoded one.
  if (!encoded && contentLength !== null && bytes.length !== contentLength) {
    return `truncated body (got ${bytes.length} of ${contentLength} bytes, type ${type}, magic ${hex(bytes)})`;
  }
  // Only a body that is none of the known image types fails: browsers sniff <img> content, so a
  // PNG served as image/jpeg still renders and must keep working.
  if (IMAGE_MAGIC.has(type) && !Array.from(IMAGE_MAGIC.values()).some((m) => startsWith(bytes, m))) {
    return `body is not ${type} (got ${bytes.length} bytes, magic ${hex(bytes)})`;
  }
  return null;
};

const parseContentLength = (raw: string | null): number | null => {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  return Number(raw);
};

/**
 * Content-script side: fetch the URL and base64 the body, timing each step.
 * The failure strings keep their original prefixes; `diag` rides alongside.
 *
 * The bytes go to WhatsApp Web as a blob it loads into an <img>; a truncated or
 * non-image body surfaces there as an opaque "Image tag load error" (#637a). So a
 * body that is shorter than Content-Length, or is not the image its type claims,
 * is refetched once and, if still wrong, fails here with size/type/magic in the message.
 */
export async function loadAssetBlob(
  url: string,
  fetchFn: typeof fetch = fetch,
  now: () => number = () => performance.now(),
): Promise<FetchAssetBlobResult> {
  const host = assetHost(url);
  const t0 = now();
  let httpStatus: number | undefined;
  try {
    for (let attempt = 1; ; attempt++) {
      const resp = await fetchFn(url, { credentials: 'omit' });
      httpStatus = resp.status;
      const fetchMs = Math.round(now() - t0);
      if (!resp.ok) {
        return { success: false, error: `fetch ${host} → HTTP ${resp.status}`, diag: { host, httpStatus, fetchMs } };
      }
      const mimeType = resp.headers.get('content-type') ?? 'application/octet-stream';
      const buf = await resp.arrayBuffer();
      const readMs = Math.round(now() - t0) - fetchMs;
      const bytes = new Uint8Array(buf);
      const problem = integrityProblem(
        bytes,
        mimeType,
        parseContentLength(resp.headers.get('content-length')),
        resp.headers.get('content-encoding') !== null,
      );
      if (problem !== null) {
        if (attempt < 2) continue;
        return { success: false, error: `fetch ${host} → ${problem}`, diag: { host, httpStatus, fetchMs, readMs, bytes: bytes.length } };
      }
      // Chunked btoa — spread-arg cap on large buffers turns this into a
      // RangeError otherwise.
      const CHUNK = 0x8000;
      let binary = '';
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
      }
      const dataBase64 = btoa(binary);
      const encodeMs = Math.round(now() - t0) - fetchMs - readMs;
      return {
        success: true,
        data: { dataBase64, mimeType, size: bytes.length },
        diag: { host, httpStatus, fetchMs, readMs, encodeMs, bytes: bytes.length },
      };
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      success: false,
      error: `fetch threw: ${msg}`,
      diag: { host, httpStatus, fetchMs: Math.round(now() - t0) },
    };
  }
}
